import { CloudUpload, UploadFile, CheckCircle, AttachFile, VisibilityOutlined } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { GlobalState } from "context/GlobalContext";
import { useState } from "react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import FileDetailsDialogue from "./FileDetailsDialogue";

const InputFileld = ({ width, column, data, setData, sx }) => {
	const handleFileChange = (e) => {
		const newFile = e.target.files[0];
		if (newFile) {
			const newFileName = column.id + "-" + newFile?.name;
			const modifiedFile = new File([newFile], newFileName, { type: newFile.type });
			setData({ ...data, [column.id]: modifiedFile });
		}
	};

	const fileSelected = data[column?.id];

	return (
		<Box sx={{ width, display: "flex", flexDirection: "column", gap: 0.8 }}>
			<Typography
				variant="body2"
				sx={{ fontWeight: 600, color: column?.disabled ? "text.disabled" : "text.secondary", ml: 0.5, fontSize: "0.8rem" }}>
				{column?.name}
			</Typography>
			<Button
				component="label"
				disableElevation
				disabled={column?.disabled}
				sx={{
					justifyContent: "flex-start",
					textTransform: "none",
					bgcolor: fileSelected ? "success.50" : "grey.50",
					color: fileSelected ? "success.main" : "text.secondary",
					border: "1px dashed",
					borderColor: fileSelected ? "success.main" : "grey.300",
					borderRadius: 2,
					py: 1.2,
					px: 2,
					"&:hover": {
						bgcolor: fileSelected ? "success.100" : "grey.100",
						borderColor: fileSelected ? "success.main" : "primary.main",
					},
					"&.Mui-disabled": {
						bgcolor: "action.disabledBackground",
						color: "text.disabled",
						borderColor: "action.disabledBackground",
					},
					...sx,
				}}
				startIcon={fileSelected ? <CheckCircle /> : <AttachFile />}>
				<Typography variant="body2" noWrap sx={{ flexGrow: 1, textAlign: "left", fontWeight: 500, fontSize: "0.85rem" }}>
					{fileSelected ? fileSelected.name.replace(`${column.id}-`, "") : "Choose file..."}
				</Typography>
				<input type="file" id={column?.id} onChange={handleFileChange} hidden disabled={column?.disabled} />
			</Button>
		</Box>
	);
};

const fileTypes = {
	PHONE_PE_REPORT: { name: "PHONE PE", key: "PHONE_PE_REPORT", id: "PHONEPE" },
	BANK_STATEMENT: { name: "BANK STATEMENT", key: "BANK_STATEMENT", id: "BANK_STATEMENT" },
	AMEX: { name: "AMEX", key: "AMEX", id: "AMEX" },
	CARD: { name: "CARD", key: "CARD", id: "CARD" },
	UPI: { name: "UPI", key: "UPI", id: "UPI" },
	MAGICPIN: { name: "MAGICPIN", key: "MAGICPIN", id: "MAGICPIN", disabled: false },
	ZOMATO: { name: "ZOMATO", key: "ZOMATO", id: "ZOMATO", disabled: true },
	CMS: { name: "CMS FILE", key: "CMS_FILE", id: "CMS", disabled: true },
	WRITER: { name: "WRITER FILE", key: "WRITER", id: "WRITER", disabled: true },
};

const FileUpload = () => {
	const [files, setFiles] = useState({});
	const [open, setOpen] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fileDialogOpen, setFileDialogOpen] = useState(false);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const handleClose = (event, reason) => {
		setErrors([]);
		setOpen(false);
	};

	const handleReset = () => {
		setFiles({});
		Object.keys(fileTypes).forEach((key) => {
			const element = document.getElementById(fileTypes[key].id);
			if (element) element.value = "";
		});
	};

	const handleViewUploadedFiles = () => {
		setFileDialogOpen(true);
	};

	const onFormSubmit = (e) => {
		e.preventDefault();

		console.log(files);
		if (Object.keys(files).length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please upload some files and try again",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		// Check for BANK_STATEMENT if UPI or REFUND is uploaded
		if ((files["UPI"] || files["REFUND"]) && !files["BANKSTATEMENT"] && !files["HDFC"]) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "BANK STATEMENT is required for UPI refunds calculation.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);

		const uploadPromises = Object.keys(files).map((fileKey) => {
			const form = new FormData();
			form.append("file", files[fileKey]);
			form.append("file_type", fileKey); // Help the backend identify the file type for the GCP job
			return pythonVmInstance.post("/cash-card-upload", form, { headers: { "Content-Type": "multipart/form-data" } });
		});

		Promise.all(uploadPromises)
			.then((responses) => {
				const combinedResponses = responses.reduce((acc, res) => acc.concat(res.data), []);
				setErrors(combinedResponses);
				setLoadingOpen(false);
				setOpen(true);
				handleReset();
			})
			.catch((err) => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Something went wrong while creating jobs",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			});
	};

	return (
		<>
			<Stack
				width="100%"
				height="100%"
				spacing={3}
				component="form"
				onSubmit={onFormSubmit}
				sx={{
					p: { xs: 2, md: 3 },
					bgcolor: "background.paper",
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
					boxSizing: "border-box",
				}}>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 2,
						pb: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
						<UploadFile sx={{ color: "primary.main", fontSize: 28 }} />
						<Box>
							<Typography variant="h5" fontWeight="600" color="text.primary">
								File Upload
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Upload one or more source files to create POS reconciliation jobs.
							</Typography>
						</Box>
					</Box>

					<Button
						size="large"
						variant="text"
						onClick={handleViewUploadedFiles}
						startIcon={<VisibilityOutlined />}
						sx={{
							width: { xs: "100%", sm: "auto" },
							borderRadius: 2,
							fontWeight: 600,
							textTransform: "none",
							py: 1.2,
							justifyContent: { xs: "center", sm: "flex-end" },
						}}>
						View Uploaded Files
					</Button>
				</Box>

				<Box
					display="grid"
					gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }}
					gap={2.5}
					pt={1}>
					{Object.keys(fileTypes).map((row) => {
						return <InputFileld key={row} data={files} setData={setFiles} column={fileTypes[row]} required width="100%" />;
					})}
				</Box>

				<Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexDirection: { xs: "column", sm: "row" }, pt: 1 }}>
					<Button
						size="large"
						variant="outlined"
						onClick={handleReset}
						sx={{
							width: { xs: "100%", sm: 140 },
							borderRadius: 2,
							fontWeight: 600,
							textTransform: "none",
							py: 1.2,
						}}>
						Reset
					</Button>
					<Button
						size="large"
						startIcon={<CloudUpload />}
						variant="contained"
						type="submit"
						disableElevation
						sx={{
							width: { xs: "100%", sm: 140 },
							borderRadius: 2,
							fontWeight: 600,
							textTransform: "none",
							py: 1.2,
						}}>
						Upload
					</Button>
				</Box>
			</Stack>
			<FileDetailsDialogue open={fileDialogOpen} onClose={() => setFileDialogOpen(false)} />
		</>
	);
};

export default FileUpload;
